import { Divider, Grid, Loader } from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";

import { createToolPhoto, deleteToolPhoto } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import { toolPhotosByToolID } from "graphql/queries";
import Gallery from "components/utils/Gallery";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import { dataURItoBlob, readFileAsDataURL } from "components/utils/functions";

const ToolPhotos = ({ toolID }) => {
  const [canUpload, canDelete] = usePermissions([
    "tool:upload-photo",
    "tool:delete-photo",
  ]);
  const [loading, setLoading] = useState(false);
  const [toolPhotos, setToolPhotos] = useState([]);
  const [toolPhotosLoading, setToolPhotosLoading] = useState(false);
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    toolPhotoId: null,
  });

  async function onCreate(fileObj) {
    try {
      setLoading(true);

      let fileSrc = "";
      let filePreview = "";

      if (typeof fileObj === "string") {
        fileSrc = `photo_${Date.now()}.jpeg`;
        const blob = dataURItoBlob(fileObj);

        await Storage.put(`tool-photos/${fileSrc}`, blob, {
          contentType: blob.type,
          level: "public",
        });

        filePreview = fileObj;
      } else if (fileObj) {
        fileSrc = `${fileObj.name
          .split(".")
          .slice(0, -1)
          .join(".")}_${Date.now()}.${fileObj.name.split(".").slice(-1)[0]}`;

        await Storage.put(`tool-photos/${fileSrc}`, fileObj, {
          contentType: fileObj.type,
          level: "public",
        });

        filePreview = await readFileAsDataURL(fileObj);
      }

      const newPhoto = (
        await API.graphql({
          query: createToolPhoto.replaceAll("__typename", ""),
          variables: {
            input: {
              toolID,
              photo: fileSrc,
            },
          },
        })
      ).data.createToolPhoto;

      setToolPhotos((prev) => [
        ...(prev ?? []),
        { ...newPhoto, photo: filePreview },
      ]);

      toast.success(strings.successAdded);
    } catch (err) {
      console.log("Error Upload Photo", err);
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  }

  async function fetchToolPhotos(id) {
    try {
      setToolPhotosLoading(true);
      const toolPhotosData = await API.graphql(
        graphqlOperation(toolPhotosByToolID, {
          toolID: id,
        })
      );
      const toolPhotosItems = toolPhotosData.data.toolPhotosByToolID.items;

      const newList = await Promise.all(
        toolPhotosItems?.map(async (toolPhoto) => {
          const src = await Storage.get(`tool-photos/${toolPhoto.photo}`, {
            level: "public",
          });
          return {
            ...toolPhoto,
            photo: src,
          };
        }) || []
      );

      setToolPhotos(newList);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setToolPhotosLoading(false);
    }
  }

  async function toolPhotoDelete(id) {
    try {
      setLoading(true);

      const result = await API.graphql(
        graphqlOperation(deleteToolPhoto, { input: { id } })
      );
      const { photo: deletedSrc } = result.data.deleteToolPhoto;
      if (deletedSrc)
        await Storage.remove(`tool-photos/${deletedSrc}`, {
          level: "public",
        });

      setToolPhotos(toolPhotos.filter((item) => item.id !== id));
      setModalDetails({
        visible: false,
        toolPhotoId: null,
      });
      toast.success(strings.successDeleted);
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchToolPhotos(toolID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolID]);

  return (
    <>
      <div>
        <div className="p-3">
          {toolPhotosLoading ? (
            <Loader />
          ) : (
            <Grid rowGap="15px" columnGap="15px">
              {Object.entries(strings.gallery.tags).map((gal, index) => (
                <>
                  {index > 0 && <Divider className="mt-3 mb-2" />}
                  <Gallery
                    images={
                      toolPhotos
                        .sort(
                          (a, b) =>
                            new Date(a.createdAt) - new Date(b.createdAt)
                        )
                        .map((photoModel) => ({
                          id: photoModel.id,
                          src: photoModel.photo,
                        })) ?? []
                    }
                    onCreate={canUpload && ((photo) => onCreate(photo))}
                    onDelete={canDelete && toolPhotoDelete}
                  />
                </>
              ))}
            </Grid>
          )}
        </div>
        <ConfirmModal
          visible={modalDetails.visible}
          body={strings.formatString(strings.modal.confirmDeleteQuestion)}
          onCancel={() =>
            setModalDetails({
              visible: false,
              toolPhotoId: null,
            })
          }
          onConfirm={() => toolPhotoDelete(modalDetails.toolPhotoId)}
        />
      </div>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Fotos de la Herramienta",
    gallery: {
      tags: {
        all: "Todos",
      },
    },
    successAdded: "Foto agregada con éxito",
    successDeleted: "Foto eliminada con éxito",
    responses: {
      errorFetch: "Error al consultar fotos de la herramienta",
      unexpectedError: "Ha ocurrido un error inesperado",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar esta foto?",
    },
  },
});

export default ToolPhotos;
